import { useState } from "react";
import { useGetIdentity, useLocaleState } from "ra-core";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  BC_ENVIRONMENTS,
  DEFAULT_BC_ENVIRONMENT,
  type BcEnvironmentCode,
} from "../misc/bcEnvironment";
import {
  VOICE_LANGUAGES,
  type VoiceLanguageCode,
} from "../voice-agent/useVoiceAgentLanguage";
import {
  completeOnboarding,
  UI_LOCALES,
  type UiLocale,
} from "../settings/userPreferences";

const UI_LOCALE_LABELS: Record<UiLocale, string> = {
  en: "English",
  fr: "Français",
  da: "Dansk",
  sv: "Svenska",
  fi: "Suomi",
};

const BC_COUNTRY_LABELS: Record<BcEnvironmentCode, string> = {
  DK: "Danish (Gobo DK)",
  SE: "Swedish (Gobo AB)",
  FI: "Finnish (Gobo Oy)",
};

type OnboardingWizardProps = {
  onComplete: () => void;
};

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const { data: identity } = useGetIdentity();
  const [, setLocale] = useLocaleState();
  const salesId = (identity?.id as number | null) ?? null;
  const { subscribe } = usePushNotifications(salesId);

  const [locale, setLocalLocale] = useState<UiLocale>("en");
  const [voiceLanguage, setVoiceLanguage] = useState<VoiceLanguageCode>("en-US");
  const [bcEnvironment, setBcEnvironment] =
    useState<BcEnvironmentCode>(DEFAULT_BC_ENVIRONMENT);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFinish = async () => {
    setSaving(true);
    setError(null);
    try {
      await completeOnboarding({
        locale,
        voiceLanguage,
        bcEnvironment,
        notificationsEnabled,
      });
      setLocale(locale);
      if (notificationsEnabled) {
        try {
          await subscribe();
        } catch {
          // Preference is saved; device permission can be granted later in Settings.
        }
      }
      onComplete();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your settings. Please try again.",
      );
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 p-4">
      <div className="w-full max-w-lg space-y-6 rounded-lg border bg-card p-6 shadow-lg">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome to Gobo CRM
          </h1>
          <p className="text-sm text-muted-foreground">
            Before you start, choose your preferred languages and defaults.
            These settings are saved to your account and follow you on every
            device.
          </p>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="onboarding-locale">Dashboard language</Label>
            <Select
              value={locale}
              onValueChange={(value) => setLocalLocale(value as UiLocale)}
            >
              <SelectTrigger id="onboarding-locale" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UI_LOCALES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {UI_LOCALE_LABELS[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onboarding-voice">Voice / agent language</Label>
            <Select
              value={voiceLanguage}
              onValueChange={(value) =>
                setVoiceLanguage(value as VoiceLanguageCode)
              }
            >
              <SelectTrigger id="onboarding-voice" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VOICE_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="onboarding-bc">
              Business Central country version
            </Label>
            <p className="text-xs text-muted-foreground">
              Which company list should be your default (Danish, Swedish, or
              Finnish)?
            </p>
            <Select
              value={bcEnvironment}
              onValueChange={(value) =>
                setBcEnvironment(value as BcEnvironmentCode)
              }
            >
              <SelectTrigger id="onboarding-bc" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BC_ENVIRONMENTS.map((env) => (
                  <SelectItem key={env.code} value={env.code}>
                    {BC_COUNTRY_LABELS[env.code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="onboarding-notifications">
                Enable task notifications
              </Label>
              <p className="text-xs text-muted-foreground">
                On by default. Your browser will ask for permission on this
                device. Other devices need permission once as well — you can
                change this later in Settings.
              </p>
            </div>
            <Switch
              id="onboarding-notifications"
              checked={notificationsEnabled}
              onCheckedChange={setNotificationsEnabled}
            />
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          type="button"
          className="w-full"
          disabled={saving}
          onClick={() => void handleFinish()}
        >
          {saving ? "Saving…" : "Finish and continue"}
        </Button>
      </div>
    </div>
  );
}
